import { useCallback, useMemo } from "react";
import { classNames, transformDateToLocalDateString } from "helpers";
import { VehicleTimelineItem } from "services/api/notifications/types";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import Tooltip from "ui/components/Tooltip/Tooltip";
import { assignLanes, getBarPlacement } from "ui/modules/notifications/timeline";
import { DEFAULT_TIMELINE_BAR_COLOR, TIMELINE_BAR_COLORS } from "./consts";
import { TimelineRowProps } from "./types";

interface PlacedItem {
  item: VehicleTimelineItem;
  startIndex: number;
  span: number;
  lane: number;
}

const TimelineRow: React.FC<TimelineRowProps> = (props) => {
  const { group, columns, onVehicleClick } = props;

  const placed = useMemo<PlacedItem[]>(() => {
    const withPlacement = group.items
      .map((item) => ({
        item,
        placement: getBarPlacement(
          new Date(item.startDate),
          new Date(item.endDate),
          columns
        ),
      }))
      .filter((entry) => entry.placement !== null);

    const lanes = assignLanes(withPlacement.map((entry) => entry.placement!));

    return withPlacement.map((entry, index) => ({
      item: entry.item,
      startIndex: entry.placement!.startIndex,
      span: entry.placement!.span,
      lane: lanes[index],
    }));
  }, [group.items, columns]);

  const laneCount = placed.reduce((max, entry) => Math.max(max, entry.lane + 1), 1);

  const handleVehicleClick = useCallback(() => {
    onVehicleClick(group.vehicle.id);
  }, [onVehicleClick, group.vehicle.id]);

  return (
    <div className="flex border-b border-slate-100 last:border-b-0">
      <div className="w-[260px] shrink-0 px-4 py-3 border-r border-slate-100">
        <VehicleCell vehicle={group.vehicle} onClick={handleVehicleClick} />
      </div>

      <div
        className="relative flex-1 grid gap-y-1 py-3"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${laneCount}, 24px)`,
        }}
      >
        {/* Okomite crte stupaca — iza traka, da mreža ostane vidljiva. */}
        {columns.map((column, index) => (
          <div
            key={index}
            className={classNames(
              "border-r border-slate-100 last:border-r-0",
              column.isToday ? "bg-sky-50" : column.isWeekend ? "bg-slate-50/70" : ""
            )}
            style={{ gridColumn: index + 1, gridRow: `1 / span ${laneCount}` }}
          />
        ))}

        {placed.map((entry) => (
          <div
            key={`${entry.item.definitionId}-${entry.item.entityDisplayName}-${entry.item.startDate}`}
            className="relative min-w-0 overflow-hidden px-0.5"
            style={{
              gridColumn: `${entry.startIndex + 1} / span ${entry.span}`,
              gridRow: entry.lane + 1,
            }}
          >
            {/* `trigger`, ne `decorate` — decorate omota sadržaj u span s iscrtkanim rubom,
                pa traka izgleda isprekidano. */}
            <Tooltip
              trigger={
                <div
                  className={classNames(
                    "h-6 w-full min-w-0 rounded-sm px-2 flex items-center overflow-hidden cursor-default",
                    TIMELINE_BAR_COLORS[entry.item.entityType] ?? DEFAULT_TIMELINE_BAR_COLOR
                  )}
                >
                  <span className="text-xs font-medium text-white truncate">
                    {entry.item.title}
                  </span>
                </div>
              }
              position="bottom-left"
              size="medium"
            >
              <div className="min-w-[240px]">
                <div className="font-medium mb-2">{entry.item.title}</div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Entitet</span>
                  <span className="font-medium text-right max-w-[150px] truncate">
                    {entry.item.entityDisplayName}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Prvo slanje</span>
                  <span className="font-medium">
                    {transformDateToLocalDateString(new Date(entry.item.startDate))}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Zadnje slanje</span>
                  <span className="font-medium">
                    {transformDateToLocalDateString(new Date(entry.item.endDate))}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Broj slanja</span>
                  <span className="font-medium">{entry.item.sendCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Kanali</span>
                  <span className="font-medium">{entry.item.channels.join(", ")}</span>
                </div>
              </div>
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineRow;
